// Mock data for Door Sensors based on real door locations in Cary
// Usage patterns are determined by door location type and crowd movement patterns

// Location types and their typical usage patterns
const LOCATION_TYPES = {
  BARK_BAR: { // Dog park area - high usage during peak times
    baseUsage: 0.8,
    peakHours: [7, 8, 17, 18, 19], // Morning and evening dog walks
    weekendMultiplier: 2.2,
    avgOpenDuration: 15, // seconds per opening
    dailyOpenings: 120 // average openings per day
  },
  ACADEMY_PAVILION: { // Event/meeting space - variable usage
    baseUsage: 0.6,
    peakHours: [10, 11, 12, 18, 19], // Event and meeting times
    weekendMultiplier: 1.8,
    avgOpenDuration: 20,
    dailyOpenings: 80
  },
  DOWNTOWN_PARK_OFFICE: { // Administrative building - business hours
    baseUsage: 0.7,
    peakHours: [9, 10, 11, 13, 14, 15], // Business hours
    weekendMultiplier: 0.3,
    avgOpenDuration: 25,
    dailyOpenings: 60
  },
  GATHERING_HOUSE: { // Community center - steady usage
    baseUsage: 0.5,
    peakHours: [9, 12, 15, 18], // Program times
    weekendMultiplier: 1.4,
    avgOpenDuration: 18,
    dailyOpenings: 90
  },
  THE_NEST: { // Community facility - consistent usage
    baseUsage: 0.6,
    peakHours: [9, 12, 15, 18], // Program times
    weekendMultiplier: 1.3,
    avgOpenDuration: 22,
    dailyOpenings: 75
  },
  PERFORMANCE_PAVILION: { // Event venue - event-based usage
    baseUsage: 0.2,
    peakHours: [19, 20, 21], // Evening performances
    weekendMultiplier: 3.0,
    avgOpenDuration: 12,
    dailyOpenings: 30
  }
};

// Helper function to determine location type from door name
const getLocationType = (doorName) => {
  const name = doorName.toLowerCase();
  if (name.includes('bark bar')) return LOCATION_TYPES.BARK_BAR;
  if (name.includes('academy pavilion')) return LOCATION_TYPES.ACADEMY_PAVILION;
  if (name.includes('downtown park') || name.includes('library office')) return LOCATION_TYPES.DOWNTOWN_PARK_OFFICE;
  if (name.includes('gathering house')) return LOCATION_TYPES.GATHERING_HOUSE;
  if (name.includes('the nest')) return LOCATION_TYPES.THE_NEST;
  if (name.includes('performance pavilion')) return LOCATION_TYPES.PERFORMANCE_PAVILION;
  return LOCATION_TYPES.GATHERING_HOUSE; // Default
};

// Helper function to calculate door usage metrics
const calculateDoorMetrics = (locationType, dayOffset = 0, hourOffset = 0, isWeekend = false, seed = 0) => {
  const currentHour = (new Date().getHours() + hourOffset) % 24;
  
  let usageMultiplier = locationType.baseUsage;
  
  // Add peak hour bonus
  if (locationType.peakHours.includes(currentHour)) {
    usageMultiplier += 0.4;
  }
  
  // Weekend multiplier
  if (isWeekend) {
    usageMultiplier *= locationType.weekendMultiplier;
  }
  
  // Add deterministic "randomness" based on seed
  const pseudoRandom1 = (Math.sin(seed * 1234) + 1) / 2;
  const pseudoRandom2 = (Math.sin(seed * 5678) + 1) / 2;
  const pseudoRandom3 = (Math.sin(seed * 9012) + 1) / 2;
  
  usageMultiplier += (pseudoRandom1 - 0.5) * 0.6; // Add variation
  usageMultiplier = Math.max(0.1, Math.min(2.0, usageMultiplier));
  
  // Calculate daily metrics
  const dailyOpenings = Math.round(locationType.dailyOpenings * usageMultiplier);
  const avgOpenDuration = Math.round(locationType.avgOpenDuration + (pseudoRandom2 - 0.5) * 10);
  const totalOpenMinutes = Math.round((dailyOpenings * avgOpenDuration) / 60);
  
  // Current door state (mostly closed)
  const isCurrentlyOpen = pseudoRandom3 < 0.05; // 5% chance of being open
  
  // Last opened time (within last few hours)
  const hoursAgo = Math.floor(pseudoRandom1 * 8) + 1; // 1-8 hours ago
  const lastOpenedAt = new Date(Date.now() - hoursAgo * 60 * 60 * 1000);
  
  // Security status
  const isLocked = !isCurrentlyOpen && (currentHour < 6 || currentHour > 22); // Locked at night
  
  return {
    isOpen: isCurrentlyOpen,
    isLocked,
    dailyOpenings,
    avgOpenDuration,
    totalOpenMinutes,
    lastOpenedAt,
    usageMultiplier
  };
};

// Helper function to generate time series data for door usage
const generateDoorTimeSeries = (locationType, seed = 0) => {
  const timeSeries = [];
  const now = new Date();
  
  // Generate 7 days of door usage data
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    const metrics = calculateDoorMetrics(locationType, -i, 0, isWeekend, seed + i);
    
    timeSeries.push({
      day: i === 0 ? 'Today' : `Day -${i}`,
      openings: metrics.dailyOpenings,
      minutes: metrics.totalOpenMinutes,
      avgDuration: metrics.avgOpenDuration,
      timestamp: date.toISOString()
    });
  }
  
  return timeSeries;
};

// Generate mock data for all door sensors
export const generateDoorMockData = () => {
  const doorDefinitions = [
    { name: "Bark Bar Bathroom 306 Door", mac: "46:ed:59:72:e6:94", lat: 35.7930411, lng: -78.7792514 },
    { name: "Bark Bar NonADA Bathroom Door", mac: "c6:38:78:b6:b5:dd", lat: 35.7930321, lng: -78.7792462 },
    { name: "Downtown Park Library Office Women's Door", mac: "a6:84:9c:55:ee:35", lat: 35.7917004, lng: -78.7803598 },
    { name: "Gathering House Bathroom 505 Door", mac: "76:fe:47:57:c5:bc", lat: 35.7929945, lng: -78.7805549 },
    { name: "Academy Pavilion Bathroom Men's Door", mac: "56:7b:f3:9f:af:ff", lat: 35.7921592, lng: -78.7811237 },
    { name: "Downtown Park Library Office Men's Bathroom Door", mac: "04:c1:03:14:56:33", lat: 35.7917246, lng: -78.7803613 },
    { name: "Gathering House 506 Door", mac: "dc:45:46:e2:bb:a7", lat: 35.7929728, lng: -78.7805541 },
    { name: "Bark Bar Bathroom 308 Door", mac: "66:0b:29:9e:d0:0a", lat: 35.7930316, lng: -78.7792626 },
    { name: "Downtown Park Library Office Family Bathroom Door", mac: "4a:cb:c6:f0:d4:dc", lat: 35.791698, lng: -78.780324 },
    { name: "The Nest Bathroom 403 Door", mac: "aa:eb:dc:07:bb:56", lat: 35.7921968, lng: -78.7800119 },
    { name: "The Nest Bathroom 402 Door", mac: "d6:56:06:16:f5:be", lat: 35.7922434, lng: -78.7799933 },
    { name: "Academy Pavilion Bathroom Women's Door", mac: "a6:84:9c:55:ee:35", lat: 35.792192, lng: -78.7811255 },
    { name: "The Nest Bathroom 401 Door", mac: "12:e5:c4:3c:4e:96", lat: 35.7922389, lng: -78.7800222 },
    { name: "Performance Pavilion Bathroom 204 Door", mac: "0e:8e:70:e8:40:82", lat: 35.7926895, lng: -78.7800269 }
  ];

  const doors = doorDefinitions.map((doorDef, index) => {
    const locationType = getLocationType(doorDef.name);
    const seed = index * 456; // Use index as seed for consistent data
    const currentMetrics = calculateDoorMetrics(locationType, 0, 0, false, seed);
    
    // Generate additional deterministic values
    const pseudoRandom1 = (Math.sin(seed * 1111) + 1) / 2;
    const pseudoRandom2 = (Math.sin(seed * 2222) + 1) / 2;
    const pseudoRandom3 = (Math.sin(seed * 3333) + 1) / 2;
    
    // Determine door type and access level
    const doorType = doorDef.name.toLowerCase().includes('bathroom') ? 'Bathroom' :
                    doorDef.name.toLowerCase().includes('office') ? 'Office' : 'General';
    
    const accessLevel = doorDef.name.toLowerCase().includes('men') ? 'Men\'s Restroom' :
                       doorDef.name.toLowerCase().includes('women') ? 'Women\'s Restroom' :
                       doorDef.name.toLowerCase().includes('family') ? 'Family Restroom' :
                       doorDef.name.toLowerCase().includes('nonada') ? 'Accessible Restroom' : 'Public Access';
    
    // Security and maintenance metrics
    const lastMaintenanceAt = new Date(Date.now() - Math.floor(pseudoRandom1 * 90) * 24 * 60 * 60 * 1000);
    const nextMaintenanceAt = new Date(Date.now() + Math.floor(pseudoRandom2 * 180) * 24 * 60 * 60 * 1000);
    
    return {
      id: `DOOR-${String(index + 1).padStart(3, '0')}`,
      name: doorDef.name,
      location: doorDef.name.replace(' Door', ''), // Remove "Door" from name
      macAddress: doorDef.mac,
      coordinates: {
        latitude: doorDef.lat,
        longitude: doorDef.lng
      },
      status: currentMetrics.isOpen ? 'Open' : 'Closed',
      isLocked: currentMetrics.isLocked,
      doorType: doorType,
      accessLevel: accessLevel,
      lastOpenedAt: currentMetrics.lastOpenedAt.toISOString(),
      locationType: Object.keys(LOCATION_TYPES).find(key => LOCATION_TYPES[key] === locationType),
      openDuration7d: generateDoorTimeSeries(locationType, seed),
      // Usage metrics
      metrics: {
        dailyOpenings: currentMetrics.dailyOpenings,
        avgOpenDuration: currentMetrics.avgOpenDuration,
        totalOpenMinutes: currentMetrics.totalOpenMinutes,
        weeklyOpenings: Math.round(currentMetrics.dailyOpenings * 7 * (0.8 + pseudoRandom1 * 0.4)),
        peakUsageHour: locationType.peakHours[Math.floor(pseudoRandom2 * locationType.peakHours.length)],
        usagePattern: pseudoRandom3 > 0.7 ? 'High' : pseudoRandom3 > 0.4 ? 'Medium' : 'Low'
      },
      // Security and maintenance
      security: {
        lockType: 'Electronic',
        accessControl: doorType === 'Office' ? 'Keycard' : 'Public',
        lastSecurityCheck: new Date(Date.now() - Math.floor(pseudoRandom1 * 7) * 24 * 60 * 60 * 1000).toISOString(),
        securityAlerts: pseudoRandom2 < 0.1 ? ['Forced entry detected'] : []
      },
      maintenance: {
        lastMaintenanceAt: lastMaintenanceAt.toISOString(),
        nextMaintenanceAt: nextMaintenanceAt.toISOString(),
        maintenanceStatus: pseudoRandom3 > 0.9 ? 'Needs Attention' : 'Good',
        batteryLevel: Math.floor(pseudoRandom1 * 30) + 70, // 70-100%
        sensorStatus: pseudoRandom2 > 0.05 ? 'Online' : 'Offline',
        lastCalibration: new Date(Date.now() - Math.floor(pseudoRandom3 * 30) * 24 * 60 * 60 * 1000).toISOString()
      }
    };
  });

  return {
    doors: doors,
    summary: {
      totalDoors: doors.length,
      openDoors: doors.filter(d => d.status === 'Open').length,
      lockedDoors: doors.filter(d => d.isLocked).length,
      needsMaintenance: doors.filter(d => d.maintenance.maintenanceStatus === 'Needs Attention').length,
      offlineSensors: doors.filter(d => d.maintenance.sensorStatus === 'Offline').length,
      securityAlerts: doors.reduce((sum, d) => sum + d.security.securityAlerts.length, 0),
      totalDailyOpenings: doors.reduce((sum, d) => sum + d.metrics.dailyOpenings, 0),
      lastUpdated: new Date().toISOString()
    }
  };
};

// Get door data by name or MAC address
export const getDoorByName = (doorName) => {
  const allData = generateDoorMockData();
  return allData.doors.find(door => 
    door.name === doorName || door.macAddress === doorName
  );
};

// Get doors by location type
export const getDoorsByLocation = (locationType) => {
  const allData = generateDoorMockData();
  return allData.doors.filter(door => door.locationType === locationType);
};

// Get doors needing attention
export const getDoorsNeedingAttention = () => {
  const allData = generateDoorMockData();
  return allData.doors.filter(door => 
    door.maintenance.maintenanceStatus === 'Needs Attention' ||
    door.maintenance.sensorStatus === 'Offline' ||
    door.security.securityAlerts.length > 0
  );
};

// Export default data for immediate use
export default generateDoorMockData();
